import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SWITCH_DELAY = 300;
const CLOSE_DELAY = 450;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController;
  #activeItem = null;
  #switchTimer;
  #closeTimer;
  #cleanupTimers = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const options = {
      signal: this.#abortController.signal,
    };

    this.addEventListener(
      'pointerenter',
      () => this.#cancelClose(),
      options
    );

    this.addEventListener(
      'pointerleave',
      () => {
        this.#cancelSwitch();
        this.#scheduleClose();
      },
      options
    );

    this.querySelectorAll('[ref="submenu[]"]').forEach((submenu) => {
      submenu.addEventListener(
        'pointerenter',
        () => {
          this.#cancelSwitch();
          this.#cancelClose();

          const item = findMenuItem(submenu);
          if (item) this.#open(item);
        },
        options
      );
    });

    this.overflowMenu?.addEventListener(
      'pointerenter',
      () => {
        this.#cancelSwitch();
        this.#cancelClose();
      },
      options
    );

    this.overflowMenu?.addEventListener(
      'pointerleave',
      () => this.#scheduleClose(),
      options
    );

    this.addEventListener(
      'focusin',
      (event) => this.activate(event),
      options
    );

    this.addEventListener(
      'focusout',
      (event) => {
        if (
          !(event.relatedTarget instanceof Node) ||
          !this.contains(event.relatedTarget)
        ) {
          this.#cancelSwitch();
          this.#scheduleClose();
        }
      },
      options
    );

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || !this.#activeItem) return;

        event.preventDefault();
        this.#activeItem.focus();
        this.#close(true);
      },
      options
    );

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#close(true);

    this.#cleanupTimers.forEach((timer, item) => {
      clearTimeout(timer);
      item.removeAttribute('data-animating');

      const submenu = findSubmenu(item);
      if (submenu) delete submenu.dataset.active;
    });

    this.#cleanupTimers.clear();
  }

  get animationDelay() {
    return Number.parseInt(this.dataset.animationDelay || '0', 10) || 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector(
      '[part="overflow"]'
    );
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    this.#cancelClose();
    this.#cancelSwitch();

    if (item === this.#activeItem) return;

    const keyboard =
      event.type === 'focus' || event.type === 'focusin';

    const hasOpenPanel =
      this.#activeItem &&
      (
        findSubmenu(this.#activeItem) ||
        this.dataset.overflowExpanded === 'true'
      );

    if (keyboard || !hasOpenPanel || event.pointerType === 'touch') {
      this.#open(item);
      return;
    }

    this.#switchTimer = setTimeout(() => {
      this.#switchTimer = undefined;

      if (item.closest('.menu-list__list-item')?.matches(':hover')) {
        this.#open(item);
      }
    }, SWITCH_DELAY);
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    const row = item?.closest('.menu-list__list-item');

    if (
      event.relatedTarget instanceof Node &&
      row?.contains(event.relatedTarget)
    ) {
      return;
    }

    this.#cancelSwitch();

    if (item === this.#activeItem) {
      this.#scheduleClose();
    }
  }

  #open(item) {
    this.#cancelSwitch();
    this.#cancelClose();

    clearTimeout(this.#cleanupTimers.get(item));
    this.#cleanupTimers.delete(item);
    item.removeAttribute('data-animating');

    if (item === this.#activeItem) return;

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    const previous = this.#activeItem;

    if (previous) {
      previous.ariaExpanded = 'false';
      previous.removeAttribute('data-animating');

      const oldSubmenu = findSubmenu(previous);
      if (oldSubmenu) delete oldSubmenu.dataset.active;
    }

    const isOverflow =
      item.closest('.menu-list__list-item')?.slot === 'overflow';

    this.dataset.overflowExpanded = String(isOverflow);
    this.#activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu =
      findSubmenu(item) ||
      (isOverflow ? this.overflowMenu : null);

    if (submenu) submenu.dataset.active = '';

    this.style.setProperty(
      '--submenu-height',
      `${submenu?.offsetHeight || 0}px`
    );

    this.style.setProperty('--submenu-opacity', '1');
  }

  #scheduleClose() {
    this.#cancelClose();

    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      this.#close();
    }, CLOSE_DELAY);
  }

  #close(force = false) {
    this.#cancelSwitch();
    this.#cancelClose();

    const item = this.#activeItem;
    if (!item) return;

    if (
      !force &&
      (
        this.overflowHovered ||
        this.contains(document.activeElement)
      )
    ) {
      return;
    }

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    this.dataset.overflowExpanded = 'false';
    this.#activeItem = null;
    this.ariaExpanded = 'false';

    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    const submenu = findSubmenu(item);

    const timer = setTimeout(() => {
      this.#cleanupTimers.delete(item);

      if (item === this.#activeItem) return;

      item.removeAttribute('data-animating');

      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150));

    this.#cleanupTimers.set(item, timer);
  }

  #cancelSwitch() {
    clearTimeout(this.#switchTimer);
    this.#switchTimer = undefined;
  }

  #cancelClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  #preloadImages = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.removeAttribute('loading');
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const row = element.closest('.menu-list__list-item');
  if (!row) return null;

  if (row.slot === 'more') {
    return (
      row.parentElement?.querySelector(
        '[slot="overflow"] [ref="menuitem"]'
      ) || null
    );
  }

  return row.querySelector('[ref="menuitem"]');
}

function findSubmenu(item) {
  return (
    item
      ?.closest('.menu-list__list-item')
      ?.querySelector('[ref="submenu[]"]') || null
  );
}